import React from "react";
import {
	Row,
	Col,
	CardTitle,
	CardBody,
	CardHeader,
	Input,
	CardText,
} from "reactstrap";
import useCurrentSaleStore from "stores/SaleStore";
import dayjs from "dayjs";
import NumberFormat from "react-number-format";
import shallow from "zustand/shallow";
import useCurrentLeaseStore from "../../../../stores/LeaseStore";

/**
 * Component for displaying the deal summary with various financial details.
 *
 * @returns {JSX.Element} The rendered component.
 */
const LeaseSummary = () => {
	const sale = useCurrentSaleStore((state) => ({ ...state }), shallow);
	const lease = useCurrentLeaseStore((state) => ({ ...state }), shallow);
	/**
	 * Component for displaying a currency value.
	 *
	 * @param {Object} props - The component props.
	 * @param {number} props.value - The currency value.
	 * @param {string} [props.className="bold"] - The CSS class for the input.
	 * @returns {JSX.Element} The rendered component.
	 */
	const CurrencyDisplay = ({ value, className = "bold" }) => (
		<NumberFormat
			readOnly
			value={value}
			thousandSeparator
			decimalScale={2}
			fixedDecimalScale
			prefix={"$"}
			isNumericString
			customInput={Input}
			className={className}
		/>
	);

	const summaryRow1 = [
		{
			CardText: "First Due Date",
			value: (
				<Input
					className="bold"
					value={
						sale.firstDueDate
							? dayjs(sale.firstDueDate).format("MM/DD/YYYY")
							: ""
					}
					disabled
				/>
			),
		},
		{
			CardText: "Final Due Date",
			value: (
				<Input
					className="bold"
					value={
						sale.finalDueDate
							? dayjs(sale.finalDueDate).format("MM/DD/YYYY")
							: ""
					}
					disabled
				/>
			),
		},
		{
			CardText: "Base Payment",
			value: (
				<CurrencyDisplay
					value={lease.basePayment}
					className="regular-payment bold"
				/>
			),
		},
		lease.additionalFee > 0 && {
			CardText: "Periodic Fee",
			value: (
				<CurrencyDisplay
					value={lease.additionalFee}
					className="regular-payment bold"
				/>
			),
		},
		{
			CardText: "Periodic Tax",
			value: (
				<CurrencyDisplay
					value={lease.monthlyTax}
					className="regular-payment bold"
				/>
			),
		},
		{
			CardText: "Payment",
			value: (
				<CurrencyDisplay value={lease.payment} className="final-payment bold" />
			),
		},
	].filter(Boolean);

	const summaryRow2 = [
		lease.leaseType === 5 && {
			CardText: "Total Lease",
			value: <CurrencyDisplay value={lease.totalLease} />,
		},

		{
			CardText: lease.leaseType === 5 ? "Depreciation" : "Total Base Payment",
			value: <CurrencyDisplay value={lease.depreciation} />,
		},
		lease.leaseType === 5 && {
			CardText: "Rent Charge",
			value: <CurrencyDisplay value={lease.totalRent} />,
		},
		{
			CardText: "Total Tax",
			value: <CurrencyDisplay value={lease.totTax} />,
		},
		{
			CardText: "Drive Off",
			value: <CurrencyDisplay value={lease.driveOff} />,
		},
		{
			CardText: "Tax on Reduction",
			value: <CurrencyDisplay value={lease.taxAmtInDriveOff} />,
		},
	].filter(Boolean);

	const summaryRow3 = [
		{
			CardText: "Total of Payments",
			value: <CurrencyDisplay value={sale.loan} />,
		},
	];

	const row1 = summaryRow1;

	return (
		<div className="deal-summary">
			<CardHeader>
				{lease.leaseType === 5 && <CardTitle tag="h3">Lease Summary</CardTitle>}
				{lease.leaseType === 6 && <CardTitle tag="h3">RTO Summary</CardTitle>}
			</CardHeader>
			<CardBody>
				<Row>
					{row1.map((obj, index) => (
						<React.Fragment key={index}>
							<Col
								xs="6" // full width on mobile
								md="3" // half width on tablet and up
								className="d-flex align-items-center text-left"
							>
								<CardText className="m-0">{obj.CardText}</CardText>
							</Col>
							<Col
								xs="6" // full width on mobile
								md="3" // half width on tablet and up
								className="d-flex align-items-center text-right justify-content-end"
							>
								{obj.value}
							</Col>
						</React.Fragment>
					))}
				</Row>
				<hr className="my-2" />
				{summaryRow2.map((obj, index) => (
					<React.Fragment key={index}>
						<Row>
							<Col className="d-flex align-items-center col-6 text-left">
								<CardText className="m-0">{obj.CardText}</CardText>
							</Col>
							<Col className="d-flex align-items-center col-6 text-right justify-content-end">
								{obj.value}
							</Col>
						</Row>
					</React.Fragment>
				))}
				<hr className="my-2" />
				{summaryRow3.map((obj, index) => (
					<React.Fragment key={index}>
						<Row>
							<Col className="d-flex align-items-center col-6 text-left">
								<h6 className="m-0">{obj.CardText}</h6>
							</Col>
							<Col className="d-flex align-items-center col-6 text-right justify-content-end deal-total">
								{obj.value}
							</Col>
						</Row>
					</React.Fragment>
				))}
			</CardBody>
		</div>
	);
};

export default LeaseSummary;
